"use client";

import { useMemo } from "react";
import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import flags from "react-phone-number-input/flags";
import { cn } from "@/lib/utils";
import { ContactPhoneCarrier } from "./carrier";

export type ContactItem = {
  id: string;
  type: "email" | "phone";
  value: string | null | undefined;
  emailType?: "personal" | "professional" | null;
  isLocal?: boolean;
  /** Raw backend source ("manual" | "upload" | "ats" | provider names) —
   * drives the provenance line copy for local contacts. */
  source?: string | null;
  createdAt?: string | null;
  contributedByName?: string | null;
  deliverability?: "deliverable" | "undeliverable" | "risk_elevated" | "unknown" | null;
  carrier?: string | null;
  isValid?: boolean | null;
  lineType?: string | null;
  feedback?: "up" | "down" | null;
};

export function PhoneFlag({
  countryCode,
  className,
}: {
  countryCode: string;
  className?: string;
}) {
  const Flag = flags[countryCode as keyof typeof flags];
  if (!Flag) return null;
  return (
    <span
      className={cn(
        "flex h-4 w-6 shrink-0 overflow-hidden bg-foreground/20 [&_svg:not([class*='size-'])]:size-full",
        className,
      )}
    >
      <Flag title={countryCode} />
    </span>
  );
}

export function formatPhone(raw: string): { formatted: string; country: string | null } {
  const parsed = parsePhoneNumberFromString(raw);
  if (parsed && parsed.country) {
    const national = parsed.formatNational();
    const code = `+${parsed.countryCallingCode}`;
    return {
      formatted: `${code} ${national}`,
      country: parsed.country,
    };
  }
  if (parsed) {
    return { formatted: parsed.formatInternational(), country: null };
  }
  return { formatted: raw, country: null };
}

export function ContactItemValuePhoneNumber({ contact }: { contact: ContactItem }) {
  const { formatted, country } = useMemo(
    () => (contact.value ? formatPhone(contact.value) : { formatted: "", country: null }),
    [contact.value],
  );
  if (!contact.value) return null;
  return (
    <div className="flex items-center gap-2 truncate text-sm">
      {country && <PhoneFlag countryCode={country} />}
      <ContactPhoneCarrier carrier={contact.carrier} />
      <span className="truncate">{formatted}</span>
    </div>
  );
}

export function ContactItemValueEmail({ contact }: { contact: ContactItem }) {
  return <span className="max-w-70 truncate text-sm">{contact.value}</span>;
}

export function ContactItemValue({ contact }: { contact: ContactItem }) {
  if (contact.type === "email") {
    return <ContactItemValueEmail contact={contact} />;
  }
  return <ContactItemValuePhoneNumber contact={contact} />;
}
